import { Download, Loader, Send } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { SendForReviewDialog } from './SendForReviewDialog';

interface MobileActionBarProps {
  templateSlug?: string;
  documentId?: string | null;
  isEditable: boolean;
  signatureFieldCount: number;
  onRequestDownload: () => void;
  onSendToSignature: () => void;
  /** A download is in flight — swaps the icon for a spinner and blocks re-clicks. */
  isDownloading?: boolean;
  hideDownload?: boolean;
  isApprovalPage?: boolean;
  isContractApproved?: boolean;
  isPendingApproval?: boolean;
}

/**
 * Fixed bottom action bar shown on mobile (`md:hidden`) — Download plus, when
 * the document is editable and has signature fields, Send for signature.
 */
export function MobileActionBar({
  templateSlug,
  documentId,
  isEditable,
  signatureFieldCount,
  onRequestDownload,
  onSendToSignature,
  isDownloading = false,
  hideDownload = false,
  isApprovalPage = false,
  isContractApproved = false,
  isPendingApproval = false,
}: MobileActionBarProps) {
  const { t } = useTranslation();
  const [isSendToSignatureModalOpen, setIsSendToSignatureModalOpen] = useState(false);

  return (
    <div className="fixed bottom-0 left-0 right-0 md:hidden z-40 bg-[hsl(var(--bg))] border-t border-border p-4 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.35)] safe-area-pb">
      <div className="flex gap-3">
        {!hideDownload && (
          <Button
            onClick={onRequestDownload}
            variant="outline"
            className="flex-1 border-border text-globe-grey"
            size={'sm'}
            disabled={isDownloading}
            aria-busy={isDownloading}
          >
            {isDownloading ? (
              <Loader className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {isDownloading
              ? t('GENERATEREVIEW_ACTIONS_PREPARINGPDF', { defaultValue: 'Preparing…' })
              : t('GENERATEREVIEW_ACTIONS_DOWNLOAD')}
          </Button>
        )}
        {isEditable && signatureFieldCount > 0 && (
          <Button
            size={'sm'}
            onClick={() => {
              if (isApprovalPage || isContractApproved || !templateSlug || !documentId) {
                onSendToSignature();
              } else {
                setIsSendToSignatureModalOpen(true);
              }
            }}
            className="flex-1 bg-selise-blue hover:bg-oxford-blue text-white hover:text-white shadow-md"
            disabled={isPendingApproval}
            title={
              isPendingApproval
                ? t('GENERATEREVIEW_PENDING_APPROVAL_SIGNATURE_DISABLED', {
                    defaultValue:
                      'The contract must be approved before it can be sent for signature.',
                  })
                : undefined
            }
          >
            <Send className="w-4 h-4 mr-2" />
            {isApprovalPage && !isContractApproved
              ? t('GENERATEREVIEW_ACTIONS_APPROVECONTRACT', {
                  defaultValue: 'Approve Contract',
                })
              : t('GENERATEREVIEW_ACTIONS_SEND')}
          </Button>
        )}
      </div>
      {!isApprovalPage && !isContractApproved && templateSlug && documentId && (
        <SendForReviewDialog
          open={isSendToSignatureModalOpen}
          onOpenChange={setIsSendToSignatureModalOpen}
          onConfirm={onSendToSignature}
          templateSlug={templateSlug}
          documentId={documentId}
        />
      )}
    </div>
  );
}
